import { useGetHashKeys } from "@/features/auth/api/hash.queries";
import type { HashKeyApiResponse } from "@/features/auth/types/hashKeys.types";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TokenDistributionTableSkeleton } from "@/components/Common/TableSkeleton";
import { useGetApiToken } from "@/features/tokens/api/token.queries";
import { useHashKeyApiKeyManagement } from "@/features/tokens/hooks/useHashKeyApiKeyManagement";
import { HashKeyExpandedRow } from "./HashKeyExpandedRow";
import { HashKeyCollapsedRow } from "./HashKeyCollapsedRow";
import { useNavigate } from "@tanstack/react-router";
import { Spinner } from "@/components/ui/spinner";
import { useTokenDistribution } from "@/features/tokens/hooks/useTokenDistribution";

export default function TokenDistribution() {
  const navigate = useNavigate();
  const {
    data: hashKeysData,
    isLoading,
    isFetching,
    refetch,
  } = useGetHashKeys();

  // Fetch all available API keys
  const { data: apiKeysData, isLoading: isLoadingApiKeys } = useGetApiToken(
    1,
    100,
    true,
  );
  const allApiKeys = (apiKeysData?.api_keys || []).filter(
    (key) => key.active === true,
  );

  // Use custom hook for API key management
  const {
    selectedApiKeys,
    keysToRemove,
    isPending,
    handleApiKeyToggle,
    handleApplyChanges,
  } = useHashKeyApiKeyManagement();

  const { expandedRows, toggleRowExpansion } = useTokenDistribution();
  const hashKeys = hashKeysData?.hash_keys || [];

  if (isLoading || isFetching) {
    return <TokenDistributionTableSkeleton />;
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col items-start justify-between space-y-5 rounded-lg bg-white p-5">
      <div className="flex w-full flex-row justify-between">
        <div className="flex w-full flex-row justify-between">
          <div className="w-full space-y-2">
            <div className="text-lg leading-5 font-bold -tracking-wide text-neutral-950">
              Hash Keys & API Keys
            </div>
            <div className="text-black-500 text-xs leading-3">
              Manage your hash keys and associated API keys
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <Button
              className="text-muted hover:text-muted flex flex-row items-center gap-1.5 p-1.5 text-xs leading-normal font-bold tracking-normal hover:bg-white/30"
              variant="minimal"
              icon
              onClick={() => {
                navigate({
                  to: "/manager/subscription-management/add-new-user",
                });
              }}
            >
              <i className="ri-add-line text-md pb-0.5 font-light"></i>
              Create New Hash Key
            </Button>
            <Button
              className="text-muted hover:text-muted gap-2 p-1.5 text-xs leading-normal font-bold tracking-normal hover:bg-white/30"
              variant="minimal"
              icon
              onClick={() => {
                refetch().then(() => {
                  toast.success("Hash keys refreshed successfully");
                });
              }}
              disabled={isLoading || isFetching}
            >
              {isFetching ? (
                <div className="flex h-full items-center justify-center text-white">
                  <Spinner size="sm" className="stroke-primary!" />
                </div>
              ) : (
                <i className="ri-loop-right-line text-sm font-light"></i>
              )}
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        <div className="scrollbar-thin overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-sm">
            <thead>
              <tr className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
                <th className="w-[35%] px-4 py-3 text-left font-medium">
                  Hash Key
                </th>
                <th className="w-[35%] px-4 py-3 text-left font-medium">
                  API Keys
                </th>
                <th className="w-[15%] px-4 py-3 text-left font-medium">
                  Status
                </th>
                <th className="w-[15%] px-4 py-3 text-left font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {hashKeys
                .sort(
                  (a: HashKeyApiResponse, b: HashKeyApiResponse) =>
                    Number(b.active) - Number(a.active),
                )
                .map((hashKey: HashKeyApiResponse) => {
                  const isExpanded = expandedRows.has(hashKey.hash_key_id);

                  return isExpanded ? (
                    <HashKeyExpandedRow
                      key={hashKey.hash_key_id}
                      hashKey={hashKey}
                      allApiKeys={allApiKeys}
                      selectedApiKeys={
                        selectedApiKeys[hashKey.hash_key_id] || []
                      }
                      keysToRemove={keysToRemove[hashKey.hash_key_id] || []}
                      isPending={isPending}
                      isLoadingApiKeys={isLoadingApiKeys}
                      onToggle={(apiKeyId, isAlreadyDistributed) =>
                        handleApiKeyToggle(
                          hashKey.hash_key_id,
                          apiKeyId,
                          isAlreadyDistributed,
                        )
                      }
                      onApplyChanges={() =>
                        handleApplyChanges(hashKey.hash_key_id)
                      }
                      onCollapse={() => toggleRowExpansion(hashKey.hash_key_id)}
                    />
                  ) : (
                    <HashKeyCollapsedRow
                      key={hashKey.hash_key_id}
                      hashKey={hashKey}
                      onExpand={() => toggleRowExpansion(hashKey.hash_key_id)}
                    />
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
